import { clsx } from 'clsx'
import type { ReactNode } from 'react'
import { useMemo } from 'react'

import type { SpeCreatorReturn } from '~/utils/SpeCreator'

import spinnerIcon from './images/spinner.svg'

interface SpinnerProps {
  className?: string
}

export function Spinner({ className }: SpinnerProps) {
  return (
    <div className={clsx('loading', 'loading--request', className)}>
      <img src={spinnerIcon} alt="Loading..." />
    </div>
  )
}

interface ErrorMessageProps {
  className?: string
  // Our error messages still come in many shapes…
  error?: Error | { error: string } | string
}

function ErrorMessage({ error, className = '' }: ErrorMessageProps) {
  if (!error) return null

  return (
    <div
      className={clsx(
        'loading',
        'loading--failure',
        'notification',
        'notification--danger',
        className,
      )}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html:
          // eslint-disable-next-line no-nested-ternary
          typeof error === 'string' ? error : 'message' in error ? error.message : error.error,
      }}
    />
  )
}

interface LoadingProps<T> {
  spe: SpeCreatorReturn<T>
  children?: ReactNode | ((payload?: T) => ReactNode)
  request?: boolean
  success?: boolean
  failure?: boolean
  placeholder?: ReactNode
  className?: string
}

// spe stands for { Status, Payload, Error }.
//
// if this.props.children doesn't depend on resulting payload, use as:
// <Loading spe={speGetProfile}>
//   <section>
//     <h3>yay! loaded!</h3>
//   </section>
// </Loading>
//
// if this.props.children depends on resulting payload - use:
// <Loading spe={speGetProfile}>{(payload) => {
//   <h3>{profile.firstName}</h3>
// }</Loading>
function Loading<T>({
  spe,
  children = null,
  request = false,
  success = false,
  failure = false,
  placeholder,
  className = '',
}: LoadingProps<T>) {
  const shouldRender = useMemo(() => {
    let enabledStatuses
    if (!request && !success && !failure) {
      enabledStatuses = ['request', 'success', 'failure']
    } else {
      enabledStatuses = [request && 'request', success && 'success', failure && 'failure']
    }

    return spe.status && enabledStatuses.includes(spe.status)
  }, [spe, request, success, failure])

  if (!shouldRender) {
    return null
  }

  if (spe.status === 'request') {
    if (placeholder) return <>{placeholder}</>
    return <Spinner className={className} />
  }

  if (spe.status === 'success') {
    if (children) {
      if (typeof children === 'function') {
        return <>{children(spe.payload)}</>
      }

      return <>{children}</>
    }

    // We need to return null (as opposed to undefined!) for new React versions not to error out.
    return null
  }

  return <ErrorMessage error={spe.error} className={className} />
}

export { ErrorMessage }
export default Loading
